import { useCustomDomain } from '@Contexts/customDomain/CustomDomainContext';
import { useSession } from '@Contexts/session/SessionContext';
import { useUserInformation } from '@Contexts/session/useUserInformation';
import { type ApiBranding } from '@Model';
import { createBrandTheme, LoadingFrame } from '@synqo/frontend-component-library';
import { type PropsWithChildren, useLayoutEffect, useMemo, useState } from 'react';
import { applyDocumentTitle, applyFavicon } from './applyBrandingToDocument';
import { BrandingContext, type IBrandingContext } from './BrandingContext';

export type BrandTheme = ReturnType<typeof createBrandTheme>;

interface IBrandingProviderProps {
    onThemeChange: (theme: BrandTheme) => void;
}

// @ai-generated 2026-10-12T09:59:59+00:00 by niklas.eicker@tngtech.com using Claude Opus 5.5 (1M context)
/**
 * Applies the branding of the current mandant, or of the custom domain before a mandant is chosen: theme, tab title, favicon and logo.
 * Shows a loading frame until it is first known, and keeps the last one while the user information reloads.
 */
export function BrandingProvider({ onThemeChange, children }: PropsWithChildren<IBrandingProviderProps>) {
    const { isLoading, data: userInformation } = useUserInformation();
    const {
        current: { mandantId },
    } = useSession();
    const [loaded, setLoaded] = useState<{ branding?: ApiBranding }>();
    const customDomain = useCustomDomain();
    const domainBranding = customDomain.kind === 'loaded' ? customDomain.branding : undefined;

    const current = userInformation?.brandings.find((b) => b.mandant_id === mandantId);
    if (!isLoading && (loaded === undefined || loaded.branding !== current)) {
        setLoaded({ branding: current });
    }

    const branding = (isLoading ? loaded?.branding : current) ?? domainBranding;
    const primaryColor = branding?.primary_color;
    const secondaryColor = branding?.secondary_color;
    const displayName = branding?.display_name;
    const faviconUrl = branding?.favicon_url;
    const logoUrl = branding?.logo_url;

    useLayoutEffect(() => {
        onThemeChange(createBrandTheme(primaryColor, secondaryColor));
    }, [onThemeChange, primaryColor, secondaryColor]);

    useLayoutEffect(() => applyDocumentTitle(displayName), [displayName]);

    useLayoutEffect(() => applyFavicon(faviconUrl), [faviconUrl]);

    const value = useMemo<IBrandingContext>(() => ({ logoUrl }), [logoUrl]);

    if (isLoading && loaded === undefined) {
        return <LoadingFrame />;
    }

    return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}
